import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Zap, TrendingUp, Target, Users, MapPin, Sparkles, LayoutDashboard, Shield, AlertTriangle, Download, BellRing, ArrowLeft, History, Route as RouteIcon, HandCoins } from 'lucide-react';
import { useInadimplenciaData } from './hooks/useInadimplenciaData';
import GlobalFilters from './components/GlobalFilters';
import { formatCurrency } from './utils';
import ExportarExcelModal from './components/ExportarExcelModal';
import type { ContextoExportacao, RecorteDaTela } from './lib/perfisExportacao';
import { useAuth } from '../../../src/contexts/AuthContext';
import ClienteCobrancaModal from '../../../src/components/ClienteCobrancaModal';
import { ClienteConsolidado } from './types';

// Import Pages
import VisaoExecutiva from './pages/VisaoExecutiva';
import PainelRecebimentos from './pages/PainelRecebimentos';
import PerfilCliente360 from './pages/PerfilCliente360';
import MatrizRisco from './pages/MatrizRisco';
import RankingDevedores from './pages/RankingDevedores';
import AnaliseSaldos from './pages/AnaliseSaldos';
import MapaGoias from './pages/MapaGoias';
import InsightsIA from './pages/InsightsIA';
import Vencidas15Dias from './pages/Vencidas15Dias';
import DebitosAntigos from './pages/DebitosAntigos';
import RotaCobranca from './pages/RotaCobranca';
import CobrancasUnificadas from './pages/CobrancasUnificadas';

interface InadimplenciaDashboardProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: string;
}

const TABS = [
  { id: 'visao', label: 'Visão Executiva', icon: LayoutDashboard },
  { id: 'recebimentos', label: 'Recebimentos', icon: TrendingUp },
  { id: 'perfil', label: 'Perfil 360', icon: Users },
  { id: 'matriz', label: 'Matriz de Risco', icon: Shield },
  { id: 'ranking', label: 'Maiores Devedores', icon: Target },
  { id: 'saldos', label: 'Saldos Acumulados', icon: Zap },
  { id: 'mapa', label: 'Mapa de Goiás', icon: MapPin },
  { id: 'insights', label: 'Insights IA', icon: Sparkles },
  { id: 'vencidas15', label: 'Vencidas 15+ dias', icon: BellRing },
  { id: 'antigos', label: 'Débitos antigos', icon: History },
  { id: 'rota', label: 'Visita presencial', icon: RouteIcon },
  { id: 'unificadas', label: 'Cobranças unificadas', icon: HandCoins },
];

const InadimplenciaDashboard: React.FC<InadimplenciaDashboardProps> = ({ isOpen, onClose, initialTab }) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'visao');
  const [selectedClientToView, setSelectedClientToView] = useState<string | null>(null);
  const [clienteParaGerenciar, setClienteParaGerenciar] = useState<ClienteConsolidado | null>(null);
  const [exportarAberto, setExportarAberto] = useState(false);
  // O que a aba aberta está mostrando com os filtros DELA (faixa de atraso,
  // rateio, busca, paradas marcadas, cliente aberto no Perfil 360). O modal de
  // exportação começa por aqui: a planilha tem que bater com a tela.
  const [recorteDaTela, setRecorteDaTela] = useState<RecorteDaTela | null>(null);

  const {
    loading, error, refetch, dataExtraction,
    filtros, setFiltros, cidadesUnicas, totalAbertoFiltrado,
    clientesFiltrados, rawFaturas, avisosPorFatura, ucsInadimplentes
  } = useInadimplenciaData();

  // Tudo que a exportação precisa ler. Montado aqui porque os dados vivem no
  // hook e o botão fica no cabeçalho, acima das abas.
  const contextoExportacao: ContextoExportacao = useMemo(() => ({
    clientes: clientesFiltrados,
    ucs: ucsInadimplentes,
    avisosPorFatura,
    filtros,
    totalAberto: totalAbertoFiltrado,
    hoje: new Date(),
    // `exportarAberto` entra de propósito: é o que faz o "hoje" (base do
    // cálculo de atraso e do carimbo do arquivo) ser o do clique, não o do
    // momento em que a Central foi aberta — ela fica horas aberta na mesa.
  }), [clientesFiltrados, ucsInadimplentes, avisosPorFatura, filtros, totalAbertoFiltrado, exportarAberto]);

  // Estável para não disparar o efeito das abas a cada render do dashboard.
  const registrarRecorte = useCallback((recorte: RecorteDaTela) => setRecorteDaTela(recorte), []);

  // Trocar de aba invalida o recorte anterior: os ids são de outra entidade
  // (cliente x fatura x unidade) e a aba nova reporta o dela no primeiro render.
  useEffect(() => { setRecorteDaTela(null); }, [activeTab]);

  // Reabertura via evento (ex.: clique numa notificação de cobrança D+15) pode pedir uma aba
  // específica mesmo com o componente já montado (isOpen indo de false -> true de novo).
  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  const handleNavigate = (tab: string, clientName?: string) => {
    setActiveTab(tab);
    if (clientName) {
      setSelectedClientToView(clientName);
    }
  };

  // "Janela de gerenciamento completo" de cobrança — mesmo painel usado em
  // Financeiro > Cobrança enviada, aberto a partir da linha do cliente aqui.
  // Restrito a quem realmente trabalha cobrança (analista/adm/gerente); outros
  // papéis (parceiro, empresa parceira) mantêm o comportamento antigo de
  // navegação para o Perfil 360.
  const podeGerenciarCobranca = profile?.role === 'analista' || profile?.role === 'adm' || profile?.role === 'gerente';
  const handleAbrirGerenciamento = (cliente: ClienteConsolidado) => setClienteParaGerenciar(cliente);

  if (!isOpen) return null;

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center h-full">
          <div className="w-8 h-8 border-4 border-[#5F6C37] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-sm font-semibold text-gray-500">Carregando dados executivos...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center h-full gap-4">
          <AlertTriangle size={48} className="text-red-500" />
          <p className="text-sm font-semibold text-red-600">{error}</p>
          <button onClick={refetch} className="px-4 py-2 bg-[#5F6C37] text-white rounded-lg text-sm font-semibold">
            Tentar Novamente
          </button>
        </div>
      );
    }

    if (activeTab === 'visao') return <VisaoExecutiva clientes={clientesFiltrados} totalAberto={totalAbertoFiltrado} />;
    if (activeTab === 'recebimentos') return <PainelRecebimentos clientes={clientesFiltrados} onNavigate={handleNavigate} onRecorteChange={registrarRecorte} />;
    if (activeTab === 'perfil') return <PerfilCliente360 clientes={clientesFiltrados} defaultClientName={selectedClientToView} onRecorteChange={registrarRecorte} />;
    if (activeTab === 'matriz') return <MatrizRisco clientes={clientesFiltrados} onNavigate={handleNavigate} />;
    if (activeTab === 'ranking') return <RankingDevedores clientes={clientesFiltrados} />;
    if (activeTab === 'saldos') return <AnaliseSaldos clientes={clientesFiltrados} />;
    if (activeTab === 'mapa') return <MapaGoias clientes={clientesFiltrados} totalAberto={totalAbertoFiltrado} />;
    if (activeTab === 'insights') return <InsightsIA clientes={clientesFiltrados} totalAberto={totalAbertoFiltrado} />;
    if (activeTab === 'vencidas15') return <Vencidas15Dias clientes={clientesFiltrados} avisosPorFatura={avisosPorFatura} onNavigate={handleNavigate} onAbrirGerenciamento={podeGerenciarCobranca ? handleAbrirGerenciamento : undefined} onRecorteChange={registrarRecorte} />;
    if (activeTab === 'antigos') return <DebitosAntigos clientes={clientesFiltrados} onNavigate={handleNavigate} onAbrirGerenciamento={podeGerenciarCobranca ? handleAbrirGerenciamento : undefined} onRecorteChange={registrarRecorte} />;
    if (activeTab === 'rota') return <RotaCobranca ucs={ucsInadimplentes} cidadeFiltro={filtros.cidade} onRecorteChange={registrarRecorte} />;
    // Sem filtros globais de propósito: a aba lista negociações, não faturas —
    // recortar por cidade/risco esconderia acordo já fechado sem motivo.
    if (activeTab === 'unificadas') return <CobrancasUnificadas />;

    return null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col bg-gray-50/95 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="flex flex-col w-full h-full bg-gray-50 shadow-2xl">
        
        {/* Header - VigorHub Design */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-[#1d220f] border-b border-[#2a3017]">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-semibold transition-colors flex-shrink-0"
              title="Voltar"
            >
              <ArrowLeft size={16} />
              Voltar
            </button>
            <div className="w-10 h-10 rounded-xl bg-[#5F6C37] flex items-center justify-center flex-shrink-0 shadow-inner">
              <Zap size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-white font-black text-lg tracking-tight">Central Executiva de Inadimplência</h1>
              <p className="text-xs font-medium text-gray-400">
                Vigor Energy · {loading ? 'Carregando...' : `${rawFaturas.length} boletos vencidos`} · Extração: {dataExtraction}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {!loading && totalAbertoFiltrado > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2a3017] border border-[#5F6C37]/30 text-sm font-bold text-[#F6A937]">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                {formatCurrency(totalAbertoFiltrado)}
              </div>
            )}
            
            {!loading && clientesFiltrados.length > 0 && (
              <button
                onClick={() => setExportarAberto(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-semibold transition-colors"
                title="Escolher o que exportar para Excel"
              >
                <Download size={15} />
                Exportar Excel
              </button>
            )}

            <button
              onClick={refetch}
              disabled={loading}
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white transition-colors disabled:opacity-50"
              title="Atualizar Dados"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button 
              onClick={onClose} 
              className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Global Filters */}
        <GlobalFilters filtros={filtros} setFiltros={setFiltros} cidades={cidadesUnicas} />

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Tabs */}
          <div className="w-56 bg-white border-r border-gray-200 flex flex-col py-4 overflow-y-auto hidden md:flex">
            <div className="px-4 mb-3 text-[10px] font-black uppercase text-gray-400 tracking-wider">Visões Disponíveis</div>
            <div className="flex flex-col gap-1 px-2">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                      active ? 'bg-[#5F6C37] text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={16} className={active ? 'text-[#F6A937]' : 'text-gray-400'} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
            
            <div className="mt-auto px-4 pt-4">
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-[10px] font-bold text-blue-800 uppercase mb-1">Dica Executiva</p>
                <p className="text-xs text-blue-700 leading-snug">
                  Use os filtros globais no topo para isolar cidades ou perfis de risco. Todas as abas são atualizadas instantaneamente.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto bg-gray-50/50">
            {renderContent()}
          </div>
        </div>

      </div>

      <ExportarExcelModal
        aberto={exportarAberto}
        onFechar={() => setExportarAberto(false)}
        abaAtiva={activeTab}
        contexto={contextoExportacao}
        recorteDaTela={recorteDaTela}
      />

      <ClienteCobrancaModal
        isOpen={!!clienteParaGerenciar}
        onClose={() => setClienteParaGerenciar(null)}
        cpfCnpjCliente={clienteParaGerenciar?.cpfCnpj || null}
        clienteNome={clienteParaGerenciar?.nome}
      />
    </div>,
    document.body
  );
};

export default InadimplenciaDashboard;
